import {
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import {
  communitySwitcherPath,
  communitySwitcherReorderPath,
} from "@circle-react/helpers/urlHelpers";
import type { CommunitySwitcher } from "@circle-react/types/CommunitySwitcher";
import { useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { useBackendProps } from "@/react/providers/AppContext";

export interface CommunitySwitcherData {
  communities: CommunitySwitcher[];
  token: string;
}

const fetchCommunitySwitcher = (): Promise<CommunitySwitcherData> =>
  reactQueryGet(communitySwitcherPath());

const reorderCommunities = (
  communities: CommunitySwitcher[],
): Promise<void> => {
  const communityOrder = communities.map(community => community.id);
  return reactQueryPut(communitySwitcherReorderPath(), {
    community_order: communityOrder,
  });
};

const communitySwitcherInvalidateCachePath =
  "/community_switchers/invalidate_cache";

// Fire-and-forget: ask the server to drop this user's session-scoped Layer 2
// cache so the next page load reseeds from fresh data.
const invalidateServerCache = (): void => {
  void reactQueryPost(communitySwitcherInvalidateCachePath).catch(() => {});
};

// We reconcile on the ordered community id list only (option A).
// Rationale: the seeded payload differs from the live API by design on a few
// fields (token is "" in the seed, community_member_id / is_current_community
// are request-computed), so a full-payload deep compare (option B) would
// false-positive on every load and defeat the cache. The id list + order is the
// signal that actually drifts (membership, switcher visibility, reorder) and
// maps directly to what the Layer 2 cache stores.
const communityOrderSignature = (communities: CommunitySwitcher[]): string =>
  communities.map(community => community.id).join(",");

const queryKey = ["COMMUNITY_SWITCHER"];

const EMPTY_COMMUNITIES: CommunitySwitcher[] = [];

export const useCommunitySwitcher = () => {
  const toast = useToast();
  const backendProps = useBackendProps();
  const hasReconciledRef = useRef(false);

  const seededCommunities = useMemo(
    () => backendProps.community_switcher_data ?? [],
    [backendProps.community_switcher_data],
  );

  const emptyCommunitySwitcherData: CommunitySwitcherData = {
    communities: seededCommunities,
    token: "",
  };

  const { data: queryData, refetch: refetchCommunities } = useQuery({
    queryKey: queryKey,
    queryFn: fetchCommunitySwitcher,
  });

  // Self-heal: when the live API community list diverges from the seeded
  // (cached) one, tell the server to bust this user's Layer 2 cache so the next
  // load is fresh. The view itself already corrects once queryData resolves;
  // this only shortens the server-side stale window from the TTL to one load.
  // Runs at most once per mount and only when seed data was actually present.
  useEffect(() => {
    if (hasReconciledRef.current) return;
    if (!queryData || seededCommunities.length === 0) return;

    hasReconciledRef.current = true;

    if (
      communityOrderSignature(queryData.communities) !==
      communityOrderSignature(seededCommunities)
    ) {
      invalidateServerCache();
    }
  }, [queryData, seededCommunities]);

  const data = queryData || emptyCommunitySwitcherData;

  const { mutate: onSortCommunities, isPending: isReorderingCommunities } =
    useMutation({
      mutationFn: reorderCommunities,
      onSuccess: async () => {
        await refetchCommunities();
      },
      onError: () => {
        toast.error(t("community_switcher_reorder.reorder_error"));
      },
    });

  const token = data.token || "";
  const communities = data.communities || EMPTY_COMMUNITIES;

  return {
    communities,
    token,
    isLoadingCommunities: false,
    isReorderingCommunities,
    refetchCommunities,
    onSortCommunities,
  };
};
